import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Sparkles, ArrowRight, Loader2, Globe, IndianRupee, DollarSign } from 'lucide-react';
import { PRICING } from '@/constants/testIds';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ROICalculator } from '@/components/ROICalculator';
import { BookDemoModal } from '@/components/BookDemoModal';

// Base monthly prices. Annual = -20% (effective monthly).
const TIERS = [
  {
    id: 'free',
    name: 'Free',
    usd: { base: 0, annual: 0, display: '$0', unit: '/forever' },
    inr: { base: 0, annual: 0, display: '₹0', unit: '/forever' },
    blurb: 'Kick the tires. Try any of 18 agents twice each — that\u2019s 36 free wow-moments, no signup.',
    features: [
      'Access to all 18 signature marketplace agents',
      '2 free tries per agent (36 total, no signup)',
      '50 messages/month after free signup',
      'Anonymous Try-Now (no card required)',
      'Community support',
    ],
    cta: 'Start free',
  },
  {
    id: 'pro',
    name: 'Pro',
    usd: { base: 49, annual: 39, display: '$49', annualDisplay: '$39' },
    inr: { base: 3999, annual: 3199, display: '₹3,999', annualDisplay: '₹3,199' },
    blurb: 'Operators shipping daily. Unlimited messages, build unlimited custom agents.',
    features: [
      'Unlimited messages across all 18 agents',
      'Unlimited custom agents (Builder)',
      'Streaming responses (real-time)',
      'Multi-model routing (GPT-5, Claude Sonnet 4.6, Gemini)',
      'Conversation history + share links',
      'Fork any marketplace agent',
      'Priority support',
    ],
    cta: 'Upgrade to Pro',
    featured: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    usd: { base: 499, annual: 399, display: '$499', annualDisplay: '$399' },
    inr: { base: 39999, annual: 31999, display: '₹39,999', annualDisplay: '₹31,999' },
    blurb: 'For teams running revenue agents at scale. SSO, audit logs, dedicated SRE.',
    features: [
      'Everything in Pro',
      'SSO + SAML',
      'Audit logs + observability',
      'Dedicated success engineer',
      'Custom SLAs + private models',
      'Programmatic API access',
      'Onboarding + training sessions',
    ],
    cta: 'Talk to sales',
  },
];

// Load Razorpay checkout script on demand
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve(false);
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loadingTier, setLoadingTier] = useState(null);
  const [billing, setBilling] = useState('monthly'); // 'monthly' | 'annual'
  const [currency, setCurrency] = useState('USD');   // 'USD' | 'INR'
  const [demoOpen, setDemoOpen] = useState(false);
  const [rzpConfig, setRzpConfig] = useState({ enabled: false });

  // Auto-detect INR for Indian users (based on browser locale)
  useEffect(() => {
    try {
      const locale = (navigator.language || '').toLowerCase();
      if (locale.includes('en-in') || locale.includes('hi') || locale.includes('india')) {
        setCurrency('INR');
      }
    } catch (_e) {
      /* noop */
    }
    api.get('/billing/razorpay/config').then((r) => setRzpConfig(r.data || { enabled: false })).catch(() => {});
  }, []);

  const payWithStripe = async (id) => {
    const r = await api.post('/billing/checkout', {
      package_id: id,
      origin_url: window.location.origin,
    });
    if (r.data.checkout_url) {
      window.location.href = r.data.checkout_url;
    } else {
      toast.error('Could not start Stripe checkout');
    }
  };

  const payWithRazorpay = async (id) => {
    if (!rzpConfig.enabled) {
      toast.error('Razorpay is not configured on this environment yet. Please drop your RAZORPAY_KEY_ID + SECRET in /app/backend/.env and redeploy.');
      return;
    }
    const scriptOk = await loadRazorpayScript();
    if (!scriptOk) {
      toast.error('Could not load Razorpay checkout script. Please check your connection.');
      return;
    }
    const r = await api.post('/billing/razorpay/order', { package_id: id });
    const { order_id, amount_paise, currency: ccy, key_id, display } = r.data;

    const options = {
      key: key_id,
      amount: amount_paise,
      currency: ccy,
      name: 'RevCore AI',
      description: `${id.toUpperCase()} plan · ${display}/mo`,
      order_id,
      prefill: {
        name: user?.name || '',
        email: user?.email || '',
      },
      theme: { color: '#FF6B1A' },
      handler: async (response) => {
        try {
          await api.post('/billing/razorpay/verify', {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          toast.success('Payment verified — plan upgraded');
          navigate(`/billing/success?session_id=${response.razorpay_order_id}&provider=razorpay`);
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Could not verify payment');
        }
      },
      modal: {
        ondismiss: () => {
          toast.message('Payment cancelled', { description: 'You can retry anytime.' });
        },
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const onTier = async (id) => {
    if (id === 'free') return navigate('/marketplace');
    if (id === 'enterprise') { setDemoOpen(true); return; }
    if (!user) return navigate('/login?next=/pricing');
    setLoadingTier(id);
    try {
      if (currency === 'INR') {
        await payWithRazorpay(id);
      } else {
        await payWithStripe(id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
    } finally {
      setLoadingTier(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20">
      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} source="pricing_enterprise" />
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="text-[10px] uppercase tracking-widest text-[#FF6B1A]">// pricing</span>
        <h1 className="display text-3xl md:text-5xl font-semibold mt-2">Built for operators. <span className="gradient-text">Priced for momentum.</span></h1>
        <p className="text-white/65 mt-3">Start free. Upgrade when it pays for itself. Which it will.</p>
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full hairline text-[10px] uppercase tracking-widest text-white/60">
          <span className="h-1.5 w-1.5 rounded-full bg-[#39FF14] pulse-green" />
          {currency === 'INR' ? 'secure razorpay checkout · test mode' : 'secure stripe checkout · test mode'}
        </div>
      </div>

      {/* Currency + Billing toggles */}
      <div className="flex flex-wrap justify-center items-center gap-3 mb-8">
        {/* Currency toggle */}
        <div className="relative inline-flex items-center hairline rounded-full p-1 bg-[hsl(225,18%,8%)]" data-testid="pricing-currency-group">
          {[
            { k: 'USD', label: 'USD', icon: DollarSign, sub: 'Stripe' },
            { k: 'INR', label: 'INR', icon: IndianRupee, sub: 'Razorpay' },
          ].map((c) => (
            <button
              key={c.k}
              onClick={() => setCurrency(c.k)}
              data-testid={`pricing-currency-${c.k.toLowerCase()}`}
              className={`relative z-10 px-4 py-2 rounded-full text-xs inline-flex items-center gap-1.5 transition-colors ${
                currency === c.k ? 'text-white' : 'text-white/55 hover:text-white/85'
              }`}
            >
              <c.icon className="h-3.5 w-3.5" />
              <span className="uppercase tracking-widest">{c.label}</span>
              <span className="text-[9px] text-white/40 ml-1 normal-case">· {c.sub}</span>
            </button>
          ))}
          <motion.span
            layout
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="absolute inset-y-1 rounded-full"
            style={{
              background: 'linear-gradient(90deg, rgba(255,107,26,0.18), rgba(57,255,20,0.15))',
              border: '1px solid rgba(255,107,26,0.45)',
              boxShadow: '0 0 18px rgba(255,107,26,0.25)',
              width: 'calc(50% - 4px)',
              left: currency === 'USD' ? 4 : 'calc(50% + 0px)',
            }}
          />
        </div>

        {/* Billing cadence toggle */}
        <div className="relative inline-flex items-center hairline rounded-full p-1 bg-[hsl(225,18%,8%)]">
          {['monthly', 'annual'].map((b) => (
            <button
              key={b}
              onClick={() => setBilling(b)}
              className={`relative z-10 px-5 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${
                billing === b ? 'text-white' : 'text-white/55 hover:text-white/85'
              }`}
              data-testid={`pricing-toggle-${b}`}
            >
              {b}
              {b === 'annual' && (<span className="ml-2 text-[10px] text-[#39FF14]">−20%</span>)}
            </button>
          ))}
          <motion.span
            layout
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="absolute inset-y-1 rounded-full"
            style={{
              background: 'linear-gradient(90deg, rgba(57,255,20,0.15), rgba(255,107,26,0.18))',
              border: '1px solid rgba(57,255,20,0.4)',
              boxShadow: '0 0 18px rgba(57,255,20,0.22)',
              width: 'calc(50% - 4px)',
              left: billing === 'monthly' ? 4 : 'calc(50% + 0px)',
            }}
          />
        </div>
      </div>

      {currency === 'INR' && !rzpConfig.enabled && (
        <div className="max-w-xl mx-auto mb-6 rounded-xl border border-[#FFB17A]/35 bg-[#FF6B1A]/10 p-3 text-center text-xs text-white/80" data-testid="pricing-razorpay-pending">
          <Globe className="h-3.5 w-3.5 inline mr-1 text-[#FFB17A]" />
          India payments go live the moment you drop your Razorpay keys into <span className="mono text-[#FFB17A]">backend/.env</span>. INR pricing shown is final.
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-5">
        {TIERS.map((t) => {
          const isFree = t.id === 'free';
          const bucket = currency === 'INR' ? t.inr : t.usd;
          const priceDisplay = isFree
            ? bucket.display
            : (billing === 'annual' ? bucket.annualDisplay : bucket.display);
          const cadence = isFree
            ? bucket.unit
            : (billing === 'annual' ? '/mo · billed annually' : '/month');
          const annualSavings = !isFree && billing === 'annual'
            ? ((bucket.base - bucket.annual) * 12)
            : null;
          const currencySymbol = currency === 'INR' ? '₹' : '$';
          return (
            <div key={t.id} data-testid={PRICING.tierCard}
              className={`card-base p-7 relative overflow-hidden ${
                t.featured ? 'border-[#FF6B1A]/40 shadow-[0_0_0_1px_rgba(255,107,26,0.30),0_0_44px_rgba(255,107,26,0.15)]' : ''
              }`}
            >
              {t.featured && (
                <span className="absolute top-4 right-4 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#FF6B1A]/15 text-[#FFB17A] border border-[#FF6B1A]/30 inline-flex items-center gap-1">
                  <Sparkles className="h-3 w-3"/> Most popular
                </span>
              )}
              <div className="text-xs uppercase tracking-widest text-white/45">{t.name}</div>
              <div className="mt-3 flex items-end gap-1">
                <span className="display text-4xl font-semibold">{priceDisplay}</span>
                <span className="text-white/55 mb-1">{cadence}</span>
              </div>
              {annualSavings && (
                <div className="text-[11px] text-[#39FF14] mt-1">Save {currencySymbol}{annualSavings.toLocaleString()}/yr vs monthly</div>
              )}
              <p className="text-sm text-white/65 mt-2 min-h-[48px]">{t.blurb}</p>
              <ul className="mt-5 space-y-2 text-sm">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-[#39FF14] mt-0.5 shrink-0" />
                    <span className="text-white/80">{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={PRICING.tierCta}
                onClick={() => onTier(t.id)}
                disabled={loadingTier === t.id}
                className={`mt-6 inline-flex items-center justify-center gap-2 w-full rounded-xl px-4 py-3 text-sm font-medium disabled:opacity-60 ${
                  t.featured ? 'btn-primary' : 'btn-secondary'
                }`}
              >
                {loadingTier === t.id ? (
                  <><Loader2 className="h-4 w-4 animate-spin"/> Opening {currency === 'INR' ? 'Razorpay' : 'Stripe'}…</>
                ) : (
                  <>{t.cta} <ArrowRight className="h-4 w-4" /></>
                )}
              </button>
              {!isFree && (
                <div className="text-[10px] text-white/40 mt-3 text-center">
                  {currency === 'INR'
                    ? <>Test cards: <span className="mono text-white/55">4111 1111 1111 1111</span> · any future date · any CVC</>
                    : <>Test card: <span className="mono text-white/55">4242 4242 4242 4242</span> · any future date · any CVC</>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-12 card-base p-6">
        <div className="text-[10px] uppercase tracking-widest text-[#39FF14] mb-2">// compare</div>
        <div className="grid md:grid-cols-4 gap-4 text-sm">
          {[
            ['Agents access', 'All 18', 'All 18 + Builder', 'All 18 + Private'],
            ['Messages', '2/agent + 50/mo', 'Unlimited', 'Unlimited'],
            ['Models', 'GPT-5 default', 'GPT-5 / Claude / Gemini', 'Custom + private'],
            ['Streaming chat', '\u2014', '\u2713', '\u2713'],
            ['Public share links', '\u2014', '\u2713', '\u2713'],
            ['Fork marketplace agents', '\u2014', '\u2713', '\u2713'],
            ['SSO + audit logs', '\u2014', '\u2014', '\u2713'],
            ['Support', 'Community', 'Priority', 'Dedicated SE'],
          ].map((row) => (
            <React.Fragment key={`compare-${row[0]}`}>
              <div className="text-white/55">{row[0]}</div>
              <div>{row[1]}</div>
              <div className="text-[#FFB17A]">{row[2]}</div>
              <div className="text-[#39FF14]">{row[3]}</div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ROI Calculator */}
      <ROICalculator onBookDemo={() => setDemoOpen(true)} />

      {/* FAQ */}
      <div className="mt-12 grid lg:grid-cols-2 gap-4">
        {[
          {
            q: 'Can I cancel anytime?',
            a: 'Yes. Pro and Enterprise are month-to-month or annual; cancel from your billing portal with one click.',
          },
          {
            q: 'Can I pay in INR?',
            a: 'Yes — switch to INR at the top of the page. We accept Indian cards, UPI, net-banking, and wallets via Razorpay. US / EU / global customers pay in USD via Stripe.',
          },
          {
            q: 'Do you offer a free trial of Pro?',
            a: 'Every visitor gets 2 free messages per agent (that\u2019s 36 total across our 18 agents), and every free account gets 50 messages/month. Pro is built to pay for itself in your first week.',
          },
          {
            q: 'Which LLMs do you support?',
            a: 'GPT-5, Claude Sonnet 4.6, and Gemini 3 Flash today. We route automatically around outages and you can pin a specific model per agent.',
          },
          {
            q: 'Is my data secure?',
            a: 'Server-side plan gating, HTTP-only cookies, isolated per-tenant data, and system prompts never leaked on share links. SSO and audit logs ship with Enterprise.',
          },
        ].map((f) => (
          <div key={f.q} className="card-base card-hover p-5">
            <div className="display text-base font-semibold">{f.q}</div>
            <p className="text-sm text-white/65 mt-2">{f.a}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
